import { UniButton } from '@/design-system/components/buttons';
import { TextBody, TextCaption } from '@/design-system/components/typography';
import { LayoutContext } from 'contexts/LayoutContext';
import { SxProp, View } from 'dripsy';
import _ from 'lodash';
import React, { useContext, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BottomSheetHeader } from '@/design-system/components/headers';
import BottomSheet, { useBottomSheetDynamicSnapPoints, BottomSheetBackdrop } from '@gorhom/bottom-sheet';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { SetCardLimitStackParamList, SetCardLimitStackRoutes } from './type';
import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { CardWithLimitProp } from '../../types';
import { CardLimitTitle } from '../../options';
import { CardLimitType } from '../../enum';

export interface SetCardLimitProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  dailyLimitType: string | undefined;
  data: any;
  account: string;
  tab?: number;
  record?: CardWithLimitProp;
  func?: (
    cardId: string,
    atmLimit?: number,
    purchaseLimit?: number,
    dailyLimit?: number,
    singleTransactionLimit?: number,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
}

export const ConfirmationNativeScreen: React.FC<SetCardLimitProps> = (props) => {
  const { onClose, onLayout, dailyLimitType, data, account, tab, record, func, onReturnData, refresh } = props;
  const { t } = useTranslation('account-management');
  const { body, textMb, textTitle, textAtmPin, buttonBox, modal, topHeader } = sxStyles;
  const [loading, setLoading] = useState(false);
  const layout = useContext(LayoutContext);
  const renderBackdrop = useCallback(
    (props) => <BottomSheetBackdrop {...props} disappearsOnIndex={0} appearsOnIndex={1} pressBehavior={'none'} enableTouchThrough={true} />,
    []
  );
  const { width, height } = layout.dimensions;
  const { animatedHandleHeight, animatedSnapPoints, animatedContentHeight, handleContentLayout } = useBottomSheetDynamicSnapPoints([
    'CONTENT_HEIGHT',
    'CONTENT_HEIGHT'
  ]);

  const navigation = useNavigation<NativeStackNavigationProp<SetCardLimitStackParamList>>();

  const [alertMessage, setAlertMessage] = useState('');

  const getPurchaseLimit = () => {
    if (dailyLimitType == CardLimitType.DailyPurchaseLimit) {
      return data.isEnabled ? null : data.value;
    }
    return record?.dailyPurchaseLimit;
  };

  const getAtmLimit = () => {
    if (dailyLimitType == CardLimitType.AtmLimit) {
      return data.isEnabled ? null : data.value;
    }
    return record?.dailyAtmLimit;
  };

  const getDailyLimit = () => {
    if (dailyLimitType == CardLimitType.DailyLimit) {
      return data.isEnabled ? null : data.value;
    }
    return record?.dailyLimit;
  };

  const getSingleTransactionLimit = () => {
    if (dailyLimitType == CardLimitType.SingleTransactionLimit) {
      return data.isEnabled ? null : data.value;
    }
    return record?.singleTransactionLimit;
  };
  return (
    <View sx={{ width, height, backgroundColor: 'transparent' }}>
      <BottomSheet
        enablePanDownToClose
        index={1}
        snapPoints={animatedSnapPoints}
        handleHeight={animatedHandleHeight}
        contentHeight={animatedContentHeight}
        backdropComponent={renderBackdrop}
        handleComponent={() => <View />}>
        <View sx={modal}>
          <View sx={{ minHeight: 280, backgroundColor: '$backgroundPrimary' }} onLayout={handleContentLayout}>
            <View sx={topHeader}>
              <BottomSheetHeader
                title={t('card-account-management::set-card-limit.title--set-card-limit-instruction-confirmation')}
                onPress={onClose}
              />
            </View>
            <View sx={body}>
              <TextBody sx={textMb} semiBold>
                {t('card-account-management::set-card-limit.text--set-card-limit-instruction-inquiry')}
              </TextBody>
              <TextCaption sx={textTitle} bold>
                {CardLimitTitle(t).find((e) => e.value == dailyLimitType)?.label}
              </TextCaption>
              <TextBody bold sx={textAtmPin}>
                {data.isEnabled
                  ? t('card-account-management::set-card-limit.text--entire-secured-credit-limit')
                  : formatAmountByDecimals({ value: data.value, decimals: 2, suffix: record?.currency || 'HKD' })}
              </TextBody>
              {!_.isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={{ mt: 5 }} />}
              <View sx={buttonBox}>
                <UniButton
                  loading={loading}
                  sx={{ width: '100%', height: 44 }}
                  labelSx={{ color: '$balck' }}
                  label={t('card-account-management::set-card-limit.button--place-instruction')}
                  onPress={() => {
                    if (func) {
                      setLoading(true);
                      func(
                        record?.id!,
                        getAtmLimit(),
                        getPurchaseLimit(),
                        getDailyLimit(),
                        getSingleTransactionLimit(),
                        (returnData) => {
                          setLoading(false);
                          onReturnData && onReturnData(returnData.data);
                          navigation.navigate(SetCardLimitStackRoutes.Placed, {
                            onLayout: onLayout,
                            data: data,
                            dailyLimitType: dailyLimitType,
                            account,
                            tab,
                            record,
                            returnData: returnData.data,
                            refresh
                          });
                        },
                        (error) => {
                          setLoading(false);
                          setAlertMessage(error?.message);
                        }
                      );
                    }
                  }}
                />
              </View>
            </View>
          </View>
        </View>
      </BottomSheet>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  modal: {
    width: '100%',
    padding: '$4'
  },
  body: {},
  textMb: {
    marginBottom: 20
  },
  textTitle: {
    fontSize: '$1',
    marginBottom: 4,
    color: '$textSecondary'
  },
  textAtmPin: {
    fontSize: '$4',
    fontWeight: 'bolder',
    color: '$textThemeSecondary'
  },
  buttonBox: {
    flex: 1,
    display: 'grid',
    gap: '$3',
    mt: '$15'
  }
};
